import { LockIcon } from 'components/icons';
import { Alert, AlertTitle } from 'components/ui/alert';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useCreateInvoiceRowMutation, useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceRowForm, InvoiceTotalsForm } from 'types/invoices';
import {
    mapInvoiceRowFormToAdminFeeCreateDto,
    mapInvoiceRowFormToInvoiceRowCreateDto,
    mapInvoiceTotalsToInvoiceTotalsFormDto,
} from '../../../util/InvoicesPageUtil';
import RecalculatingOverlay from '../../components/RecalculatingOverlay';
import InvoiceMatterTotals from './InvoiceMatterTotals';

interface Props {
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    isLoading: boolean;
    /** The one case whose card to draw; without it every case's, under the shared-payment copy's lock notice. */
    matterId?: number;
}

/** A shared-payment copy's notice that its totals rows are locked, drawn once above the cards. */
export function CaseTotalsLockNotice({ invoice }: { invoice: InvoiceData }) {
    const { isSharedPaymentCopy } = useSharedPayment(invoice);
    return isSharedPaymentCopy ? (
        <Alert role="note" className="bg-neutral-tint text-foreground border-transparent">
            <LockIcon aria-hidden />
            <AlertTitle className="font-normal">
                <FormattedMessage
                    id="invoices.payers.totals.rows-locked-description"
                    values={{ count: invoice.sharedPaymentGroup?.invoiceCount ?? 1 }}
                />
            </AlertTitle>
        </Alert>
    ) : null;
}

/**
 * Case totals cards on the new design's Cases tab: the Totals tab's per-case blocks, with their Add and Edit. A
 * credit note's only read.
 */
export function CaseTotals({ invoice, classifiers, isInViewMode, isLoading, matterId }: Props) {
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const formMethods = useForm<Partial<InvoiceTotalsForm>>({
        values: mapInvoiceTotalsToInvoiceTotalsFormDto(invoiceTotals),
        defaultValues: mapInvoiceTotalsToInvoiceTotalsFormDto(invoiceTotals),
    });
    const { isCredit } = useInvoice(invoice);
    const [createInvoiceRow] = useCreateInvoiceRowMutation();

    const handleAddRow = useCallback(
        (data: InvoiceRowForm) => void createInvoiceRow(mapInvoiceRowFormToInvoiceRowCreateDto(data, invoice.id)),
        [createInvoiceRow, invoice.id]
    );
    const handleAddAdminFee = useCallback(
        (data: InvoiceRowForm) => void createInvoiceRow(mapInvoiceRowFormToAdminFeeCreateDto(data, invoice.id)),
        [createInvoiceRow, invoice.id]
    );

    return (
        <FormProvider {...formMethods}>
            <div className="relative flex flex-col gap-(--card-gap)">
                {matterId === undefined ? <CaseTotalsLockNotice invoice={invoice} /> : null}
                <InvoiceMatterTotals
                    invoiceTotals={invoiceTotals}
                    matterId={matterId}
                    classifiers={classifiers}
                    isInViewMode={isInViewMode || isCredit || isLoading}
                    handleAddRow={handleAddRow}
                    handleAddAdminFee={handleAddAdminFee}
                    invoice={invoice}
                />
                <RecalculatingOverlay invoiceId={invoice.id} />
            </div>
        </FormProvider>
    );
}
